import { useState, useEffect } from "react";
import Seo from "../Seo.jsx";
import pageConfig from "../../config/pageConfig.js";
import { formatMoney } from "../../helpers.js";
import { calcMinProfit } from "./calculations.js";
import { Clipboard, ClipboardCheck } from 'lucide-react';

const RefundCalculator = () => {
  const meta = pageConfig.refundCalculator?.seo || {};

  // INPUTS
  const [stake, setStake] = useState(null);
  const [backOdds, setBackOdds] = useState(null);
  const [layOdds, setLayOdds] = useState(null);
  const [commission, setCommission] = useState(0);
  const [fieldSize, setFieldSize] = useState(null);
  const [refundPlaces, setRefundPlaces] = useState(1);
  const [refundAmount, setRefundAmount] = useState(null);
  const [retention, setRetention] = useState(80);

  // DERIVED VALUES
  const [layStake, setLayStake] = useState(null);
  const [profitIfWin, setProfitIfWin] = useState(null);
  const [profitIfNoRefund, setProfitIfNoRefund] = useState(null);
  const [profitIfRefund, setProfitIfRefund] = useState(null);
  const [chanceRefund, setChanceRefund] = useState(null);
  const [impliedOdds, setImpliedOdds] = useState(null);
  const [riskRewardRatio, setRiskRewardRatio] = useState(null);

  const [evOdds, setEvOdds] = useState(null);
  const [evMoney, setEvMoney] = useState(null);
  const [evPercentage, setEvPercentage] = useState(null);

  // Clipboard copy state
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const S = parseFloat(stake);
    const B = parseFloat(backOdds);
    const L = parseFloat(layOdds);
    const comm = parseFloat(commission);
    const runners = parseInt(fieldSize, 10);
    const rPlaces = parseInt(refundPlaces, 10);
    const R = parseFloat(refundAmount);
    const ret = parseFloat(retention) / 100;

    if (!S || B <= 1 || L <= 1 || runners < 1 || rPlaces < 1 || !R) {
      setLayStake(null);
      return;
    }

    // Normal matched bet outcomes
    const normal = calcMinProfit(S, B, L, comm, false, false);
    setLayStake(parseFloat(normal.potExchangeWinnings.toFixed(2)));
    setProfitIfWin(parseFloat(normal.profitIfBookieWins.toFixed(2)));
    setProfitIfNoRefund(parseFloat(normal.profitIfExchangeWins.toFixed(2)));

    // Profit if refund: refundAmount * retentionRate minus qualifying loss
    const qualifyingLoss = normal.profitIfExchangeWins < 0 ? Math.abs(normal.profitIfExchangeWins) : 0;
    const refundProfit = (R * ret) - qualifyingLoss;
    setProfitIfRefund(parseFloat(refundProfit.toFixed(2)));

    // Chance of refund
    const pRefund = rPlaces / runners;
    setChanceRefund(`${(pRefund * 100).toFixed(0)}%`);

    // Implied Odds = (Potential Profit / Qualifying Loss) + 1
    // Implied Odds = (Potential Profit / Qualifying Loss) + 1
const impl = qualifyingLoss > 0
? (refundProfit / qualifyingLoss) + 1
: null;
setImpliedOdds(impl != null ? impl.toFixed(2) : null);

// Risk : Reward ratio = Potential Profit / Qualifying Loss
const rr = qualifyingLoss > 0
? refundProfit / qualifyingLoss
: null;
setRiskRewardRatio(rr != null ? rr.toFixed(2) : null);


    // Expected Value
    const EV = pRefund * refundProfit + (1 - pRefund) * normal.profitIfExchangeWins;
    setEvMoney(parseFloat(EV.toFixed(2)));
    setEvPercentage(parseFloat(((EV / S) * 100).toFixed(2)));

    // EV Odds using same formula as extra-place calc
    const evO = (runners * 1.5) / rPlaces;
    setEvOdds(rPlaces > 0 ? evO.toFixed(2) : null);

  }, [stake, backOdds, layOdds, commission, fieldSize, refundPlaces, refundAmount, retention]);

  // Clipboard handlers
  const getLayStakeLabel = () => layStake != null ? `£${formatMoney(layStake)}` : '';
  const copyToClipboard = () => {
    if (layStake == null) return;
    navigator.clipboard.writeText(layStake.toString()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  const handleKeyDown = (e) => {
    if (e.key.toLowerCase() === "c") copyToClipboard();
  };
  const formatOutcome = val => val == null ? "" : `${val < 0 ? "–" : ""}£${Math.abs(val).toFixed(2)}`;
  const formatEV = (money, perc) => {
    if (money == null || perc == null) return "";
    const m = money < 0 ? `–£${Math.abs(money).toFixed(2)}` : `£${money.toFixed(2)}`;
    const p = perc < 0 ? `–${Math.abs(perc).toFixed(2)}%` : `${perc.toFixed(2)}%`;
    return `${m} (${p})`;
  };

  return (
    <>
      <Seo title={meta.title} description={meta.description} url={pageConfig.refundCalculator?.route || "/calculators"}/>
      <div className="container">
        <h1 className="title with-subhead">Refund Offer Analyser</h1>
        <h4 className="subhead">Evaluate offers that refund your stake on place finishes.</h4>

        <div className="inline-fields">
          <div className="input-group-inline">
            <label>Stake:</label>
            <div className="input-prefix-suffix only-prefix">
              <span className="prefix">£</span>
              <input type="number" placeholder="10.00" onKeyDown={handleKeyDown} value={stake} onChange={e => setStake(e.target.value)} />
            </div>
          </div>
          <div className="input-group-inline">
            <label>Back Odds:</label>
            <input type="number" placeholder="5.0" onKeyDown={handleKeyDown} value={backOdds} onChange={e => setBackOdds(e.target.value)} />
          </div>
          <div className="input-group-inline">
            <label>Lay Odds:</label>
            <input type="number" placeholder="5.2" onKeyDown={handleKeyDown} value={layOdds} onChange={e => setLayOdds(e.target.value)} />
          </div>
          <div className="input-group-inline">
            <label>Exchange Commission:</label>
            <div className="input-prefix-suffix only-suffix">

                <input type="number" step="0.1" placeholder="2" onKeyDown={handleKeyDown} value={commission} onChange={e => setCommission(e.target.value)} />
                <span className="suffix">%</span>
            </div>
          </div>
        </div>

        <div className="inline-fields">
          <div className="input-group-inline">
            <label>Field Size: <span className="info-icon">
                i
                <span className="tooltip-text">
                The total number of runners in the market.
                </span>
              </span></label>
            <input type="number" placeholder="12" value={fieldSize} onChange={e => setFieldSize(e.target.value)} />
          </div>
          <div className="input-group-inline">
            <label>Refund Places: <span className="info-icon">
                i
                <span className="tooltip-text">
                Number of places that trigger a stake refund (e.g. 1 for 2nd place only).
                </span>
              </span></label>
            <input type="number" placeholder="1" value={refundPlaces} onChange={e => setRefundPlaces(e.target.value)} />
          </div>
        </div>

        <div className="inline-fields">
          <div className="input-group-inline">
            <label>Refund Amount: <span className="info-icon">
                i
                <span className="tooltip-text">
                How much cash or free‑bet value you get back if it places.
                </span>
              </span></label>
            <div className="input-prefix-suffix only-prefix">
              <span className="prefix">£</span>
              <input type="number" placeholder="10" onKeyDown={handleKeyDown} value={refundAmount} onChange={e => setRefundAmount(e.target.value)} />
            </div>
          </div>
          <div className="input-group-inline">
            <label>Free Bet Retention: <span className="info-icon">
                i
                <span className="tooltip-text">
                The percentage of the refunded free‑bet you expect to convert into profit. This is 100% for cash refunds with no wagering.
                </span>
              </span></label>
            <div className="input-prefix-suffix only-suffix">

            <input type="number" step="1" placeholder="80" onKeyDown={handleKeyDown} value={retention} onChange={e => setRetention(e.target.value)} />
            <span className="suffix">%</span>
            </div>
          </div>
        </div>

   

{layStake !== null && (
          <>
            <div
              className={`result-box copyable ${copied ? "glow" : ""}`}
              onClick={copyToClipboard}
              title="Click to copy lay stake"
              style={{ cursor: "pointer", display: "flex", justifyContent: "space-between" }}
            >
              <h5 className="outcome-main">
                <span>You could lay: {getLayStakeLabel()}</span>
              </h5>
              {copied ? <ClipboardCheck size={24} color="#edff00" /> : <Clipboard size={24} color="var(--secondary)" />}
            </div>
            <div className="result-box">
              <h3 style={{marginTop: "0"}}>Outcomes</h3>
              <p style={{color: "var(--text2)"}}>
                For <strong>{formatOutcome((-Math.min(profitIfWin,profitIfNoRefund)))}</strong>, you can buy a <strong>{chanceRefund}</strong> chance of making a profit of <strong>{formatOutcome(profitIfRefund)}</strong>.
                </p>
              <div className="outcome-line">
                <span className="outcome-label">Profit if Wins:</span>
                <span className="outcome-value" style={{ color: profitIfWin > 0 ? "var(--negative)" : "var(--positive)" }}>{formatOutcome(profitIfWin)}</span>
              </div>
              <div className="outcome-line">
                <span className="outcome-label">Profit if Refund Triggered:</span>
                <span className="outcome-value" style={{ color: profitIfRefund > 0 ? "var(--negative)" : "var(--positive)" }}>{formatOutcome(profitIfRefund)}</span>
              </div>
              <div className="outcome-line">
                <span className="outcome-label">Profit if Lose:</span>
                <span className="outcome-value" style={{ color: profitIfNoRefund > 0 ? "var(--negative)" : "var(--positive)" }}>{formatOutcome(profitIfNoRefund)}</span>
              </div>
              </div>

            <div className="result-box">
              <h3 style={{marginTop: "0"}}>Results</h3>
              <div className="outcome-line">
                <span className="outcome-label">Chance of Refund:</span>
                <span className="outcome-value">{chanceRefund}</span>
              </div>
              <div className="outcome-line">
  <span className="outcome-label">Risk : Reward Ratio:</span>
  <span className="outcome-value">1:{riskRewardRatio ?? "N/A"}</span>
</div>
<div className="outcome-line">
  <span className="outcome-label">Implied Odds:</span>
  <span className="outcome-value">{impliedOdds ?? "N/A"}</span>
</div>

              <div className="outcome-line">
                <span className="outcome-label">EV Odds:</span>
                <span className="outcome-value">{evOdds ?? "N/A"}</span>
              </div>
              <div className="outcome-line">
                <span className="outcome-label">Expected Value:</span>
                <span className={`outcome-value ${evMoney < 0 ? "negative" : "positive"}`}>
                  {formatEV(evMoney, evPercentage)}
                </span>
              </div>
              <div className="outcome-line">
                <span className="outcome-label">Profitability:</span>
                <span className="outcome-value" style={{ color: evMoney < 0 ? "var(--negative)" : "var(--positive)" }}>
                  {evMoney > 0 ? "✓ Theoretically Profitable" : "✕ Not Profitable"}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default RefundCalculator;
